/**
 * Matrix-View UX-Prototypes — nur zu Anschauung/Entscheidung.
 * Route: /ui-kit/matrix-prototypes
 *
 * Drei Varianten mit identischen Mock-Daten:
 *   A — Reine Matrix (wie heute, aber zentralisiert)
 *   B — Hybrid (Tree+Detail Default, Matrix-Modus als opt-in)
 *   C — Tree + Permission-Badges (Matrix entfaellt)
 */

import { useMemo, useState } from 'react'
import { Folder, Lock, Plus, Shield, Users, X } from 'lucide-react'
import { Badge, Button, Card, PageHeader, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { cn } from '@/lib/utils.ts'

// ─── Mock-Daten ────────────────────────────────────────────────────────────

type Perm = 'R' | 'W' | null
type GroupType = 'AB' | 'SW' | 'LI' | 'FU'

interface Group {
  id: string
  name: string
  type: GroupType
}

interface FolderNode {
  id: string
  name: string
  parentId: string | null
  level: number // 0 = share-root
}

const GROUPS: Group[] = [
  { id: 'ab-geschaefts', name: 'Geschäftsführung', type: 'AB' },
  { id: 'ab-it', name: 'IT', type: 'AB' },
  { id: 'ab-vertrieb', name: 'Vertrieb', type: 'AB' },
  { id: 'ab-buchhaltung', name: 'Buchhaltung', type: 'AB' },
  { id: 'ab-personal', name: 'Personal', type: 'AB' },
  { id: 'sw-finanzen', name: 'SW_Finanzen', type: 'SW' },
  { id: 'sw-crm', name: 'SW_CRM', type: 'SW' },
  { id: 'fu-lesen', name: 'FU_Lesen', type: 'FU' },
  { id: 'fu-archiv', name: 'FU_Archiv', type: 'FU' },
  { id: 'li-drucker', name: 'LI_Drucker', type: 'LI' },
]

const FOLDERS: FolderNode[] = [
  { id: 'r-unternehmen', name: 'Unternehmen', parentId: null, level: 0 },
  { id: 'f-gf', name: 'Geschäftsleitung', parentId: 'r-unternehmen', level: 1 },
  { id: 'f-gf-vertraege', name: 'Verträge', parentId: 'f-gf', level: 2 },
  { id: 'f-gf-protokolle', name: 'Protokolle', parentId: 'f-gf', level: 2 },
  { id: 'f-buchhaltung', name: 'Buchhaltung', parentId: 'r-unternehmen', level: 1 },
  { id: 'f-bu-monatlich', name: 'Monatsabschlüsse', parentId: 'f-buchhaltung', level: 2 },
  { id: 'f-bu-belege', name: 'Belege', parentId: 'f-buchhaltung', level: 2 },
  { id: 'f-bu-belege-2024', name: '2024', parentId: 'f-bu-belege', level: 3 },
  { id: 'f-bu-belege-2025', name: '2025', parentId: 'f-bu-belege', level: 3 },
  { id: 'f-vertrieb', name: 'Vertrieb', parentId: 'r-unternehmen', level: 1 },
  { id: 'f-v-angebote', name: 'Angebote', parentId: 'f-vertrieb', level: 2 },
  { id: 'f-v-kunden', name: 'Kundendaten', parentId: 'f-vertrieb', level: 2 },
  { id: 'f-it', name: 'IT-Administration', parentId: 'r-unternehmen', level: 1 },
  { id: 'f-it-dokus', name: 'Dokumentationen', parentId: 'f-it', level: 2 },
  { id: 'f-it-pwlist', name: 'Passwortlisten', parentId: 'f-it', level: 2 },
  { id: 'f-personal', name: 'Personalakten', parentId: 'r-unternehmen', level: 1 },
]

// Mock-Permissions: Map<folderId, Map<groupId, 'R'|'W'|null>>
const INITIAL_PERMS: Record<string, Record<string, Perm>> = {
  'r-unternehmen': { 'fu-lesen': 'R' },
  'f-gf': { 'ab-geschaefts': 'W' },
  'f-gf-vertraege': { 'ab-geschaefts': 'W', 'ab-it': 'R' },
  'f-buchhaltung': { 'ab-buchhaltung': 'W', 'ab-geschaefts': 'R' },
  'f-bu-belege': { 'ab-buchhaltung': 'W', 'ab-geschaefts': 'R', 'sw-finanzen': 'R' },
  'f-vertrieb': { 'ab-vertrieb': 'W' },
  'f-v-angebote': { 'ab-vertrieb': 'W', 'ab-geschaefts': 'R', 'sw-crm': 'R' },
  'f-it': { 'ab-it': 'W' },
  'f-it-pwlist': { 'ab-it': 'W' },
  'f-personal': { 'ab-personal': 'W', 'ab-geschaefts': 'R' },
}

const TYPE_COLOR: Record<GroupType, string> = {
  AB: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  SW: 'bg-blue-100 text-blue-800 border-blue-200',
  LI: 'bg-amber-100 text-amber-800 border-amber-200',
  FU: 'bg-emerald-100 text-emerald-800 border-emerald-200',
}

function nextPerm(p: Perm): Perm {
  if (p === null) return 'R'
  if (p === 'R') return 'W'
  return null
}

function permClass(p: Perm): string {
  if (p === 'W') return 'bg-emerald-100 text-emerald-800 font-bold'
  if (p === 'R') return 'bg-blue-50 text-blue-700 font-medium'
  return 'bg-white text-gray-300'
}

function permLabel(p: Perm): string {
  if (p === 'W') return 'RW'
  if (p === 'R') return 'R'
  return '·'
}

// ─── Prototype A: Reine Matrix ────────────────────────────────────────────

function MatrixVariant({ perms, setPerm }: {
  perms: Record<string, Record<string, Perm>>
  setPerm: (folderId: string, groupId: string, value: Perm) => void
}) {
  const typeGroups = useMemo(() => {
    const byType = new Map<GroupType, Group[]>()
    for (const g of GROUPS) {
      if (!byType.has(g.type)) byType.set(g.type, [])
      byType.get(g.type)!.push(g)
    }
    return Array.from(byType.entries())
  }, [])

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="text-xs border-separate border-spacing-0">
        <thead>
          <tr className="align-bottom">
            <th
              rowSpan={2}
              className="sticky left-0 z-20 bg-gray-100 border border-gray-300 text-left px-3 py-2 font-semibold text-gray-700 w-[260px] min-w-[260px] align-middle"
            >
              Ordner
            </th>
            {typeGroups.map(([type, grps]) => (
              <th
                key={type}
                colSpan={grps.length}
                className={cn('border border-gray-300 px-2 py-1 text-center align-bottom font-bold tracking-wide', TYPE_COLOR[type])}
              >
                <span className="font-mono mr-1">{type}</span>
                {grps.length}
              </th>
            ))}
          </tr>
          <tr className="align-bottom">
            {typeGroups.flatMap(([, grps]) =>
              grps.map((g) => (
                <th
                  key={g.id}
                  className="bg-gray-50 border border-gray-300 text-center align-bottom w-[32px] min-w-[32px] max-w-[32px] px-0 pt-1 pb-4 font-medium text-gray-700"
                >
                  <div className="flex items-end justify-center" style={{ minHeight: '90px' }}>
                    <div
                      className="text-[11px] whitespace-nowrap"
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                      title={g.name}
                    >
                      {g.name}
                    </div>
                  </div>
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {FOLDERS.map((f) => (
            <tr key={f.id} className="hover:bg-gray-50/40">
              <td
                className="sticky left-0 z-10 bg-white border border-gray-200 px-3 py-1.5 text-gray-800"
                style={{ paddingLeft: `${12 + f.level * 14}px` }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Folder className="w-3.5 h-3.5 text-gray-400" />
                  {f.name}
                </span>
              </td>
              {typeGroups.flatMap(([, grps]) =>
                grps.map((g) => {
                  const p = perms[f.id]?.[g.id] ?? null
                  return (
                    <td
                      key={g.id}
                      className={cn('border border-gray-200 text-center w-[32px] min-w-[32px] max-w-[32px] px-0 py-0 cursor-pointer select-none hover:ring-1 hover:ring-brand-400', permClass(p))}
                      onClick={() => setPerm(f.id, g.id, nextPerm(p))}
                      title={`${f.name} · ${g.name} → ${permLabel(p) || 'leer'}`}
                    >
                      {permLabel(p)}
                    </td>
                  )
                }),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Prototype C: Tree + Permission-Badges ────────────────────────────────

function TreeBadges({ perms, setPerm }: {
  perms: Record<string, Record<string, Perm>>
  setPerm: (folderId: string, groupId: string, value: Perm) => void
}) {
  const [editing, setEditing] = useState<string | null>(null)

  return (
    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white">
      {FOLDERS.map((f) => {
        const folderPerms = perms[f.id] ?? {}
        const entries = Object.entries(folderPerms).filter(([, v]) => v !== null) as Array<[string, 'R' | 'W']>
        const isEditing = editing === f.id
        return (
          <div key={f.id} className="flex items-start gap-3 px-3 py-2 hover:bg-gray-50/50">
            <div style={{ paddingLeft: `${f.level * 14}px` }} className="flex items-center gap-1.5 min-w-[260px] pt-0.5">
              <Folder className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-800">{f.name}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1 flex-1">
              {entries.length === 0 && !isEditing && (
                <span className="text-caption text-gray-400 italic">Keine expliziten Rechte</span>
              )}
              {entries.map(([gid, perm]) => {
                const g = GROUPS.find((x) => x.id === gid)
                if (!g) return null
                return (
                  <span
                    key={gid}
                    className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px]', TYPE_COLOR[g.type])}
                  >
                    <span className="font-mono text-[9px] opacity-60">{g.type}</span>
                    {g.name}
                    <span className="font-bold text-[10px]">{perm === 'W' ? '✎' : '👁'}</span>
                    {isEditing && (
                      <button
                        onClick={() => setPerm(f.id, gid, null)}
                        className="ml-0.5 opacity-60 hover:opacity-100"
                        title="Entfernen"
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    )}
                  </span>
                )
              })}
              {isEditing && (
                <select
                  className="px-1.5 py-0.5 border border-gray-300 rounded text-[11px] bg-white"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      setPerm(f.id, e.target.value, 'R')
                      e.target.value = ''
                    }
                  }}
                >
                  <option value="">+ Gruppe</option>
                  {GROUPS.filter((g) => !folderPerms[g.id] || folderPerms[g.id] === null).map((g) => (
                    <option key={g.id} value={g.id}>{g.type} · {g.name}</option>
                  ))}
                </select>
              )}
            </div>
            <Button
              variant={isEditing ? 'primary' : 'ghost'}
              size="xs"
              onClick={() => setEditing(isEditing ? null : f.id)}
            >
              {isEditing ? 'Fertig' : 'Bearbeiten'}
            </Button>
          </div>
        )
      })}
    </div>
  )
}

// ─── Prototype B: Hybrid (Tree+Detail + Mode-Switch) ──────────────────────

function HybridView({ perms, setPerm }: {
  perms: Record<string, Record<string, Perm>>
  setPerm: (folderId: string, groupId: string, value: Perm) => void
}) {
  const [mode, setMode] = useState<'tree' | 'matrix'>('tree')
  const [selected, setSelected] = useState<string>('f-buchhaltung')

  if (mode === 'matrix') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="xs" onClick={() => setMode('tree')}>
            ← Zur Tree-Ansicht
          </Button>
          <span className="text-caption text-gray-500">Matrix-Modus: Bulk-Änderungen über alle Ordner × Gruppen.</span>
        </div>
        <MatrixVariant perms={perms} setPerm={setPerm} />
      </div>
    )
  }

  const selectedFolder = FOLDERS.find((f) => f.id === selected)
  const selectedPerms = perms[selected] ?? {}

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end gap-2">
        <Button variant="secondary" size="xs" onClick={() => setMode('matrix')} leadingIcon={<Shield className="w-3 h-3" />}>
          Matrix-Modus (Bulk)
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(280px,40%)_1fr] gap-3">
        {/* Tree */}
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white">
          {FOLDERS.map((f) => {
            const count = Object.values(perms[f.id] ?? {}).filter(Boolean).length
            return (
              <button
                key={f.id}
                onClick={() => setSelected(f.id)}
                style={{ paddingLeft: `${12 + f.level * 14}px` }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50',
                  selected === f.id && 'bg-brand-50 text-brand-700',
                )}
              >
                <Folder className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="flex-1 truncate">{f.name}</span>
                {count > 0 && <Badge variant="neutral" size="sm">{count}</Badge>}
              </button>
            )
          })}
        </div>

        {/* Detail */}
        <Card>
          <Card.Header>
            <Card.Title className="flex items-center gap-2 text-body">
              <Folder className="w-4 h-4" />
              {selectedFolder?.name ?? 'Kein Ordner gewählt'}
            </Card.Title>
          </Card.Header>
          <Card.Body className="space-y-2">
            <p className="text-caption text-gray-500">Gruppen mit Zugriff auf diesen Ordner:</p>
            <div className="space-y-1">
              {GROUPS.map((g) => {
                const p = selectedPerms[g.id] ?? null
                return (
                  <div key={g.id} className="flex items-center gap-2 py-1">
                    <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] min-w-[130px]', TYPE_COLOR[g.type])}>
                      <span className="font-mono text-[9px] opacity-60">{g.type}</span>
                      {g.name}
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant={p === null ? 'secondary' : 'ghost'}
                        size="xs"
                        onClick={() => setPerm(selected, g.id, null)}
                      >
                        Kein Zugriff
                      </Button>
                      <Button
                        variant={p === 'R' ? 'primary' : 'ghost'}
                        size="xs"
                        onClick={() => setPerm(selected, g.id, 'R')}
                      >
                        Lesen
                      </Button>
                      <Button
                        variant={p === 'W' ? 'primary' : 'ghost'}
                        size="xs"
                        onClick={() => setPerm(selected, g.id, 'W')}
                      >
                        Lesen + Schreiben
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────

export function MatrixPrototypesPage() {
  const [perms, setPerms] = useState(INITIAL_PERMS)

  const setPerm = (folderId: string, groupId: string, value: Perm) => {
    setPerms((prev) => {
      const next = { ...prev }
      next[folderId] = { ...(next[folderId] ?? {}) }
      if (value === null) delete next[folderId][groupId]
      else next[folderId][groupId] = value
      return next
    })
  }

  const stats = {
    folders: FOLDERS.length,
    groups: GROUPS.length,
    assigned: Object.values(perms).reduce((sum, m) => sum + Object.values(m).filter(Boolean).length, 0),
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Matrix-View UX-Prototypen"
        description="Drei Varianten für die Zugriffsmatrix, identische Demo-Daten. Zellen/Badges sind interaktiv — Permission-Zyklus: leer → R → RW → leer."
        breadcrumbs={[
          { label: 'UI-Kit', to: '/ui-kit' },
          { label: 'Matrix-Prototypen' },
        ]}
      />

      <Card>
        <Card.Body>
          <div className="flex flex-wrap gap-4 text-caption text-gray-500">
            <span><b>{stats.folders}</b> Ordner (4 Ebenen, flach gerendert)</span>
            <span><b>{stats.groups}</b> Gruppen (AB/SW/FU/LI)</span>
            <span><b>{stats.assigned}</b> aktive Permissions</span>
          </div>
        </Card.Body>
      </Card>

      <Tabs defaultValue="a">
        <TabsList>
          <TabsTrigger value="a"><Users className="w-3.5 h-3.5 mr-1" />A — Reine Matrix</TabsTrigger>
          <TabsTrigger value="b"><Plus className="w-3.5 h-3.5 mr-1" />B — Hybrid</TabsTrigger>
          <TabsTrigger value="c"><Lock className="w-3.5 h-3.5 mr-1" />C — Tree + Badges</TabsTrigger>
        </TabsList>

        <TabsContent value="a">
          <Card>
            <Card.Header>
              <Card.Title>Variante A — Reine Matrix (aktuell, zentralisiert)</Card.Title>
            </Card.Header>
            <Card.Body className="space-y-3">
              <p className="text-body text-gray-600">
                Ordner als Zeilen, Gruppen als Spalten mit 2-Level-Header. Zelle = Permission-Cycle (Klick). Großes Big-Picture
                auf einen Blick, aber horizontaler Scroll bei vielen Gruppen.
              </p>
              <div className="rounded-lg bg-green-50 border border-green-200 p-2 text-caption text-green-800">
                <b>Stärke:</b> alle Permissions auf einen Blick · Bulk-Änderungen pro Spalte · Export als Excel natürlich.
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-caption text-amber-800">
                <b>Schwäche:</b> horizontaler Scroll · Gruppen-Namen vertikal unleserlich · Fehleranfällig bei vielen Klicks.
              </div>
              <MatrixVariant perms={perms} setPerm={setPerm} />
            </Card.Body>
          </Card>
        </TabsContent>

        <TabsContent value="b">
          <Card>
            <Card.Header>
              <Card.Title>Variante B — Hybrid (Tree-Detail Default, Matrix opt-in)</Card.Title>
            </Card.Header>
            <Card.Body className="space-y-3">
              <p className="text-body text-gray-600">
                Default: Tree links, Detail-Panel rechts mit klaren „Kein Zugriff / Lesen / Lesen + Schreiben"-Buttons je Gruppe.
                Für Bulk-Änderungen Wechsel in den Matrix-Modus (gleiche Matrix wie Variante A).
              </p>
              <div className="rounded-lg bg-green-50 border border-green-200 p-2 text-caption text-green-800">
                <b>Stärke:</b> klar lesbar · explizite Permission-Labels · kein Cycle-Ratespiel · Matrix weiter verfügbar für Power-User.
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-caption text-amber-800">
                <b>Schwäche:</b> zwei Views zu pflegen · Mode-Switch kann verwirren · „welche Ordner hat Gruppe X?" erfordert Matrix-Modus.
              </div>
              <HybridView perms={perms} setPerm={setPerm} />
            </Card.Body>
          </Card>
        </TabsContent>

        <TabsContent value="c">
          <Card>
            <Card.Header>
              <Card.Title>Variante C — Tree + Permission-Badges (Matrix entfällt)</Card.Title>
            </Card.Header>
            <Card.Body className="space-y-3">
              <p className="text-body text-gray-600">
                Tree mit Ordnern. Neben jedem Ordner als Badge-Liste die Gruppen mit Zugriff (Icon-Suffix 👁 = Lesen, ✎ = Schreiben).
                Klick auf „Bearbeiten" pro Ordner → Gruppen entfernen / hinzufügen inline.
              </p>
              <div className="rounded-lg bg-green-50 border border-green-200 p-2 text-caption text-green-800">
                <b>Stärke:</b> kompakter · keine vertikalen Header · intuitiv für „welche Gruppen dürfen hier?".
              </div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-caption text-amber-800">
                <b>Schwäche:</b> „welche Ordner darf Gruppe X?" braucht Filter/Suche · keine Bulk-Änderungen über alle Ordner hinweg · Excel-Export weniger naheliegend.
              </div>
              <TreeBadges perms={perms} setPerm={setPerm} />
            </Card.Body>
          </Card>
        </TabsContent>
      </Tabs>

      <Card>
        <Card.Body className="space-y-2 text-caption text-gray-600">
          <p className="font-medium text-gray-800">Entscheidungshilfe:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>A</b> wenn: bestehende Admin-Workflows, Excel-Kompatibilität und Bulk-Operations kritisch sind.</li>
            <li><b>B</b> wenn: neue User sollen nicht mit der Matrix konfrontiert werden, Admins sollen weiterhin Matrix nutzen können.</li>
            <li><b>C</b> wenn: der primäre Use-Case „Berechtigungen pro Ordner verwalten" ist, und Excel-Export / Bulk-Matrix selten.</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  )
}
